export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(`${iso}T00:00:00`)
  if (isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  })
}

export function fmtTime(t: string | null | undefined): string {
  if (!t) return ''
  const [h, m] = t.split(':')
  const hh = parseInt(h ?? '0', 10)
  if (isNaN(hh)) return t
  const ampm = hh >= 12 ? 'PM' : 'AM'
  const h12 = hh % 12 === 0 ? 12 : hh % 12
  return `${h12}:${m ?? '00'} ${ampm}`
}

// Trip currencies: US/CAN dollars + Costa Rican colón. USD keeps the bare $
// it always had; anything unknown falls back to it too. Colones have no
// cents in practice, so CRC drops decimals.
const CURRENCY_SYMBOLS: Record<string, string> = { USD: '$', CAD: 'CA$', CRC: '₡' }

export function fmtMoney(
  n: number | null | undefined,
  currency?: string,
  symbolOverrides?: Record<string, string>,
): string {
  if (n == null) return ''
  const cur = currency ?? 'USD'
  const symbols = symbolOverrides
    ? { ...CURRENCY_SYMBOLS, ...symbolOverrides }
    : CURRENCY_SYMBOLS
  const symbol = symbols[cur] ?? '$'
  // Dollars always show cents (US$59.20 keeps its zero); colones never do.
  const decimals = cur === 'CRC' ? 0 : 2
  return `${symbol}${n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`
}

export function fmtDateTime(isoDate: string, isoTime: string | null | undefined): string {
  const d = fmtDate(isoDate)
  const t = isoTime ? fmtTime(isoTime) : ''
  return [d, t].filter(Boolean).join(' · ')
}

/** Best-effort display name from an email local part: "paul.guy@" → "Paul Guy". */
export function nameFromEmail(email: string | null | undefined): string {
  const local = (email ?? '').split('@')[0]
  if (!local) return ''
  return local
    .split(/[._\-+]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

export interface TextChunk {
  text: string
  url?: string
}

// Full URLs, or bare domains with a conservative TLD allowlist (so "7 bd / 7 ba"
// or "Jan 23 → Jan 30" never turn into links).
const URL_RE =
  /https?:\/\/[^\s]+|\b(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:com|net|org|io|co|travel|info|biz|dev|app|ai|xyz|me|tv|fm|site|online|link)(?:\/[^\s]*)?/gi

/** Split free text into plain-text and URL chunks (for linkifying notes). */
export function splitOnUrls(text: string): TextChunk[] {
  const chunks: TextChunk[] = []
  let last = 0
  for (const m of text.matchAll(URL_RE)) {
    const start = m.index ?? 0
    if (start > 0 && text[start - 1] === '@') continue // email local part, not a link
    // Trailing sentence punctuation isn't part of the URL — leave it as text.
    const raw = m[0].replace(/[.,;:!?)'"]+$/, '')
    if (raw.length === 0) continue
    if (start > last) chunks.push({ text: text.slice(last, start) })
    const url = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`
    chunks.push({ text: raw, url })
    last = start + raw.length
  }
  if (last < text.length) chunks.push({ text: text.slice(last) })
  return chunks
}

const MAPS_RE = /^\s*google maps:\s*['"”’]?(.+?)['"“’]?\s*$/i

/** "Google Maps: 'Ranch Villa Arenal'" → search URL for that place, or null. */
export function mapsSearchUrl(text: string): string | null {
  const m = text.match(MAPS_RE)
  if (!m) return null
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m[1].trim())}`
}

const TAB_ROUTES: Record<string, string> = {
  dashboard: '/', calendar: '/calendar', people: '/people', flights: '/flights',
  transport: '/transport', activities: '/activities', ideas: '/ideas',
  tasks: '/tasks', money: '/money', admin: '/admin',
}

const TAB_REF_RE =
  /((?:see|check|visit)\s+)?(the\s+)?(dashboard|calendar|people|flights|transport|activities|ideas|tasks|money|admin)\s+tab\b/i

export interface TabRef {
  route: string
  lead: string   // text before the link
  label: string  // linked text, e.g. "see People tab"
}

/** "…— see People tab" → in-app link to that page, or null. */
export function tabRef(text: string): TabRef | null {
  const m = text.match(TAB_REF_RE)
  if (!m) return null
  const route = TAB_ROUTES[m[3].toLowerCase()]
  if (!route) return null
  return { route, lead: text.slice(0, m.index ?? 0), label: m[0] }
}
